import {useCallback, useMemo, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {useFetchHook} from '@hooks/query-hook';
import {useStoreContext} from '@store/store-context';
import {userName} from '@utils/user-name';
import FeedbackFeed, {
  normaliseFeedbackResponse,
  normaliseRequestResponse,
} from '../feedback-feed';
import {FeedbackRequest} from '../feedback-items';
import {FeedbackDismissModal} from '../feedback-dismiss-modal';
import {
  excludeDismissedFeedbackRequests,
  rememberDismissedFeedbackRequest,
} from '../feedback-dismissed-requests';
import {FeedbackFilterDivider, FeedbackFilters} from '../feedback-page.styles';
import {
  FeedbackDateControl,
  FeedbackFilterControl,
} from '../feedback-filter-control';
import {useFeedbackWrittenHook} from './feedback-direct-reports-hook';

const idOf = (value: string | {id?: string} | undefined) =>
  typeof value === 'string' ? value : value?.id || '';

export const FeedBackDirectReports = observer(() => {
  const {
    controller,
    filters,
    sortBy,
    setSortBy,
    dateRange,
    setDateRange,
  } = useFeedbackWrittenHook();
  const [type, setType] = useState<'all' | 'feedback' | 'praise' | 'request'>(
    'all',
  );
  const [selectedRecipient, setSelectedRecipient] = useState('');
  const [
    requestToDelete,
    setRequestToDelete,
  ] = useState<FeedbackRequest | null>(null);
  const [deletedRequestIds, setDeletedRequestIds] = useState<string[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const {
    authStore: {auth},
    usersStore: {users},
  } = useStoreContext();
  const workspaceId = auth.user.workspace?.id || 'unknown-workspace';

  const fetchSentFeedback = useCallback(
    async () => controller.fetchDirectReportsFeedback(filters),
    [controller, filters],
  );
  const fetchSentRequests = useCallback(
    async () =>
      controller.getFeedbackRequests({
        user: auth.user.id,
        startDate: filters.startDate,
        endDate: filters.endDate,
        include: 'reviewer,user',
      }),
    [auth.user.id, controller, filters.endDate, filters.startDate],
  );

  const feedbackQuery = useFetchHook(
    ['feedback-written', filters],
    fetchSentFeedback,
    {refetchOnWindowFocus: false},
  );
  const requestsQuery = useFetchHook(
    [
      'feedback-requests-sent',
      auth.user.id,
      filters.startDate,
      filters.endDate,
    ],
    fetchSentRequests,
    {refetchOnWindowFocus: false},
  );

  const feedback = normaliseFeedbackResponse(feedbackQuery.data).filter(
    (item) =>
      !(item.visibility === 'private' && item.reviewer !== auth.user.id),
  );
  const requests = excludeDismissedFeedbackRequests(
    normaliseRequestResponse(requestsQuery.data),
    workspaceId,
    auth.user.id,
  ).filter(
    ({id, reviewer}) =>
      !deletedRequestIds.includes(id) &&
      (!selectedRecipient || idOf(reviewer) === selectedRecipient),
  );
  const recipientOptions = useMemo(
    () => [
      {value: '', label: 'Everyone'},
      ...users
        .filter(({id}) => id !== auth.user.id)
        .map((user) => ({value: user.id, label: userName(user)})),
    ],
    [auth.user.id, users],
  );
  const filteredFeedback = useMemo(() => {
    if (type === 'request') return [];
    return feedback.filter((item) => {
      const isPraise = Boolean(item.isPraise || item.group?.length);
      const recipientIds = isPraise
        ? (item.group || []).map(idOf)
        : [idOf(item.user)];

      if (selectedRecipient && !recipientIds.includes(selectedRecipient)) {
        return false;
      }
      if (type === 'praise') return isPraise;
      if (type === 'feedback') return !isPraise;
      return true;
    });
  }, [feedback, selectedRecipient, type]);

  const deleteRequest = useCallback(async () => {
    if (!requestToDelete) return;

    setIsDeleting(true);
    try {
      const deleted = await controller.deleteFeedbackRequest(
        requestToDelete.id,
      );
      if (deleted) {
        rememberDismissedFeedbackRequest(
          workspaceId,
          auth.user.id,
          requestToDelete.id,
        );
        setDeletedRequestIds((requestIds) => [
          ...requestIds,
          requestToDelete.id,
        ]);
        setRequestToDelete(null);
        await requestsQuery.refetch();
      }
    } finally {
      setIsDeleting(false);
    }
  }, [auth.user.id, controller, requestToDelete, requestsQuery, workspaceId]);

  return (
    <div>
      <FeedbackFilters aria-label="Sent feedback filters">
        <div className="feedback-filter-group">
          <FeedbackFilterControl
            ariaLabel="Filter feedback recipient"
            label="To"
            options={recipientOptions}
            value={selectedRecipient}
            onChange={setSelectedRecipient}
            searchable
          />
          <FeedbackFilterDivider aria-hidden="true" />
          <FeedbackFilterControl
            ariaLabel="Filter sent item type"
            label="Type"
            options={[
              {value: 'all', label: 'All'},
              {value: 'feedback', label: 'Feedback'},
              {value: 'praise', label: 'Praise'},
              {value: 'request', label: 'Request'},
            ]}
            value={type}
            onChange={(value) =>
              setType(value as 'all' | 'feedback' | 'praise' | 'request')
            }
          />
        </div>
        <div className="feedback-filter-group">
          <FeedbackFilterControl
            ariaLabel="Sort sent items"
            label="Sort"
            options={[
              {value: 'desc', label: 'Newest First'},
              {value: 'asc', label: 'Oldest First'},
            ]}
            value={sortBy}
            onChange={setSortBy}
          />
          <FeedbackFilterDivider aria-hidden="true" />
          <FeedbackDateControl
            dateRange={dateRange}
            setDateRange={setDateRange}
          />
        </div>
      </FeedbackFilters>
      <FeedbackFeed
        variant="sent"
        data={filteredFeedback}
        requests={type === 'feedback' || type === 'praise' ? [] : requests}
        isLoading={feedbackQuery.isLoading || requestsQuery.isLoading}
        sortOrder={sortBy as 'asc' | 'desc'}
        pageKey={JSON.stringify([filters, selectedRecipient, type, sortBy])}
        emptyState={
          dateRange.preset !== 'all' || selectedRecipient || type !== 'all'
            ? {
                title: 'No sent items found',
                description:
                  'Try changing the recipient, type, or date filters.',
              }
            : undefined
        }
        onDeleteRequest={setRequestToDelete}
      />
      <FeedbackDismissModal
        action="delete"
        request={requestToDelete}
        isLoading={isDeleting}
        onClose={() => setRequestToDelete(null)}
        onConfirm={deleteRequest}
      />
    </div>
  );
});
